import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Checkbox, Radio, RadioGroup, FormControlLabel } from '@mui/material';
import { getShortIdForOrder } from '../../../utils/orderUtils';
import { STORAGE_LOCATIONS } from '../models/OrderStorageLocations';
import { BagLocation } from '@m4a3/api-client';

// Update the bag options type to use a number
type BagQuantityType = 1 | 2;

interface OrderStorageLocationModalProps {
  open: boolean;
  order: any;
  onDone: (locations: BagLocation[], bagQuantity: BagQuantityType) => void;
}

const OrderStorageLocationModal = ({
  open,
  order,
  onDone
}: OrderStorageLocationModalProps) => {
  const [selectedLocations, setSelectedLocations] = useState<BagLocation[]>([]);
  const [selectedBagQuantity, setSelectedBagQuantity] = useState<BagQuantityType | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  
  const orderNumber = order ? getShortIdForOrder(order) : '';

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedLocations([]);
      setSelectedBagQuantity(undefined);
      setError(null);
    }
  }, [open]);

  // Update selected locations when order changes or modal opens
  useEffect(() => {
    if (order && open) {
      console.log("Modal opened with order:", order);
      // Initialize with existing storage locations from the order
      const orderStorageLocations = order.bagLocations || [];
      setSelectedLocations(orderStorageLocations);
      
      // Set the bag quantity based on the number of locations
      if (orderStorageLocations.length === 1) {
        console.log("Setting bag quantity to 1");
        setSelectedBagQuantity(1);
      } else if (orderStorageLocations.length === 2) {
        console.log("Setting bag quantity to 2");
        setSelectedBagQuantity(2);
      } else {
        console.log("No bag quantity set");
        setSelectedBagQuantity(undefined);
      }
      
      setError(null);
    }
  }, [order, open]);

  // Debug logging for state changes
  useEffect(() => {
    console.log("selectedBagQuantity changed:", selectedBagQuantity);
  }, [selectedBagQuantity]);

  const handleLocationToggle = (locationId: BagLocation) => {
    // Don't proceed if bag quantity isn't selected yet
    if (!selectedBagQuantity) return;
  
    setSelectedLocations(prev => {
      // If already selected, always allow deselecting
      if (prev.includes(locationId)) {
        return prev.filter(id => id !== locationId);
      } 
      
      // For new selections, check against the bag quantity limit
      // Allow selecting up to 2 locations for 2 bags (but also allow just 1 location)
      const maxLocations = selectedBagQuantity;
      
      // If we've already selected the maximum number of locations, replace the first one
      if (prev.length >= maxLocations) {
        // Replace the first location with the new one
        const newLocations = [...prev];
        newLocations[0] = locationId;
        return newLocations;
      }
      
      // Otherwise, add the new location
      return [...prev, locationId];
    });
    
    setError(null); // Clear error when user makes a selection
  };

  const handleBagQuantityChange = (value: string) => {
    // Convert string value to number and set state
    const numValue = parseInt(value, 10);
    console.log("Setting bag quantity to:", numValue);
    setSelectedBagQuantity(numValue as BagQuantityType);
    
    // Adjust selected locations to match the bag quantity
    setSelectedLocations(prev => {
      if (numValue === 1 && prev.length > 1) {
        // If changing to 1 bag but have multiple locations, keep only the first location
        return [prev[0]];
      } else if (prev.length === 0) {
        // If no locations are selected yet, that's fine
        return prev;
      }
      
      // For 2 bags, allow keeping whatever locations are already selected
      return prev;
    });
    
    setError(null); // Clear error when user makes a selection
  };

  const handleDone = () => {
    if (!selectedBagQuantity) {
      setError('Please select a bag option before continuing');
      return;
    }
    
    if (selectedLocations.length === 0) {
      setError('Please select at least one storage location');
      return;
    }
    
    // Make sure the number of locations is valid
    if (selectedBagQuantity === 1 && selectedLocations.length !== 1) {
      setError('Please select exactly 1 storage location for 1 bag');
      return;
    }
    
    if (selectedBagQuantity === 2 && selectedLocations.length > 2) {
      setError('Please select at most 2 storage locations for 2 bags');
      return;
    }
    
    // If there are 2 bags but only 1 location, duplicate the location
    let locationsToSubmit = selectedLocations;
    if (selectedBagQuantity === 2 && selectedLocations.length === 1) {
      locationsToSubmit = [selectedLocations[0], selectedLocations[0]];
    }
    
    onDone(locationsToSubmit, selectedBagQuantity);
  };

  if (!open || !order) return null;
  
  const handleBackdropClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!selectedBagQuantity) {
      setError('Please select a bag option before continuing');
    } else if (selectedLocations.length === 0) {
      setError('Please select at least one storage location');
    } else if (selectedBagQuantity === 1 && selectedLocations.length !== 1) {
      setError('Please select exactly 1 storage location for 1 bag');
    } else if (selectedBagQuantity === 2 && selectedLocations.length > 2) {
      setError('Please select at most 2 storage locations for 2 bags');
    } else {
      // Show a blue text reminder instead of closing
      setError('Please tap done to finish');
    }
    // Never allow backdrop click to close the modal
    return;
  };

  // Converting the number to string for RadioGroup
  const radioValue = selectedBagQuantity !== undefined 
    ? selectedBagQuantity.toString() 
    : '';
    
  console.log("Current RadioGroup value:", radioValue);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1400,
      }}
      onClick={handleBackdropClick}
    >
      <Paper
        elevation={3}
        sx={{
          width: 'calc(100% - 32px)',
          maxWidth: '600px', // Increased width to accommodate two columns
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: 3,
          margin: '16px',
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold', textAlign: 'center' }}>
          Where did you leave {orderNumber}?
        </Typography>

        <Box sx={{ display: 'flex', mb: 4, gap: 3 }}>
          {/* Left Column - Bag Quantity */}
          <Box sx={{ flex: '0 0 40%', borderRight: '1px solid #f0f0f0', pr: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              How many bags?
            </Typography>
            
            <RadioGroup
              value={radioValue}
              onChange={(e) => handleBagQuantityChange(e.target.value)}
            >
              {[
                { value: '1', label: '1 bag', emoji: '👜' },
                { value: '2', label: '2 bags', emoji: '👜' }
              ].map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={
                    <Radio 
                      sx={{ 
                        color: '#d0d0d0',
                        '&.Mui-checked': {
                          color: '#4285F4',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box 
                        sx={{ 
                          width: 48, 
                          height: 48, 
                          borderRadius: '50%', 
                          backgroundColor: '#f5f5f5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          mr: 3
                        }}
                      >
                        {option.emoji}
                      </Box>
                      <Typography variant="h5">{option.label}</Typography>
                    </Box>
                  }
                  sx={{
                    margin: 0,
                    py: 2,
                    borderBottom: '1px solid #f0f0f0',
                    width: '100%',
                  }}
                />
              ))}
            </RadioGroup>
          </Box>

          {/* Right Column - Storage Locations */}
          <Box sx={{ flex: '1 1 60%' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Storage location
            </Typography>
            
            {STORAGE_LOCATIONS.map((location) => (
              <Box 
                key={location.id}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 2,
                  borderBottom: '1px solid #f0f0f0'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box 
                    sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: '50%', 
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      mr: 3
                    }}
                  >
                    {location.emoji}
                  </Box>
                  <Typography variant="h5">{location.label}</Typography>
                </Box>
                <Checkbox 
                  checked={selectedLocations.includes(location.id)}
                  onChange={() => handleLocationToggle(location.id)}
                  sx={{ 
                    '& .MuiSvgIcon-root': { fontSize: 32 },
                    color: '#d0d0d0',
                    '&.Mui-checked': {
                      color: '#4285F4',
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {error && (
          <Typography 
            variant="body1" 
            color={error === 'Please tap done to finish' ? 'primary' : 'error'}
            sx={{ 
              textAlign: 'center', 
              mb: 2,
              fontWeight: 500
            }}
          >
            {error}
          </Typography>
        )}

        <Button
          variant="contained"
          fullWidth
          onClick={handleDone}
          sx={{
            mt: error ? 0 : 2,
            mb: 1,
            py: 2,
            backgroundColor: '#4285F4',
            color: 'white',
            borderRadius: '50px',
            textTransform: 'none',
            fontSize: '20px',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: '#2a75f3',
            },
          }}
        >
          Done
        </Button>
      </Paper>
    </Box>
  );
};

export default OrderStorageLocationModal;